import { useEffect, useRef } from 'react'

interface UseInfiniteScrollProps {
  hasNextPage?: boolean
  isFetching: boolean
  fetchNextPage: () => void
  threshold?: number
}

/**
 * Custom hook to handle infinite scroll with IntersectionObserver
 * @param hasNextPage - Whether there are more pages to load
 * @param isFetching - Whether data is currently being fetched
 * @param fetchNextPage - Function to fetch the next page
 * @param threshold - Intersection threshold (default: 0.1)
 * @returns ref - Ref to attach to the sentinel element
 */
export const useInfiniteScroll = ({
  hasNextPage,
  isFetching,
  fetchNextPage,
  threshold = 0.1,
}: UseInfiniteScrollProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sentinelRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage()
        }
      },
      { threshold }
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetching, fetchNextPage, threshold])

  return sentinelRef
}
